import React, { useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { ErrorMessage } from "widgets";
import Loader from "shared/Icons/Loader/Loader";
import {
  findGradeRowIndex,
  idsMatch,
  sanitizeGradeCountInput,
} from "helpers/schoolUtils";
import { shouldShowFormError } from "utils/utils";

import formStyles from "../TenantForm/TenantForm.module.scss";
import styles from "./SchoolForm.module.scss";

const GradeWiseFields = ({
  values,
  errors,
  touched,
  gradeOptions,
  isGradeListLoading,
  isErrorOnGradeList,
  errorMessageOnFetchGradeList,
  submitAttempted,
  setFieldValue,
  handleBlur,
}) => {
  const showError = (fieldTouched, fieldError) =>
    shouldShowFormError(fieldTouched, fieldError, submitAttempted);
  const selectedOptions = useMemo(
    () =>
      (values.gradewiseStudentCount || [])
        .map((row) => {
          const option = gradeOptions.find((grade) =>
            idsMatch(grade.value, row.gradeId),
          );
          return option
            ? { ...option, gradeId: row.gradeId, studentCount: row.studentCount }
            : null;
        })
        .filter(Boolean),
    [gradeOptions, values.gradewiseStudentCount],
  );
  const getRowMeta = (gradeId) => {
    const index = findGradeRowIndex(values.gradewiseStudentCount, gradeId);
    return {
      index,
      rowErrors: index === -1 ? undefined : errors.gradewiseStudentCount?.[index],
      rowTouched: index === -1 ? undefined : touched.gradewiseStudentCount?.[index],
    };
  };
  const updateGradeRows = (nextRows) =>
    setFieldValue("gradewiseStudentCount", nextRows, true);
  const handleSelectionChange = (_, newOptions) => {
    updateGradeRows(
      newOptions.map((option) => {
        const existing = values.gradewiseStudentCount.find((row) =>
          idsMatch(row.gradeId, option.value),
        );
        return {
          gradeId: option.value,
          studentCount: existing?.studentCount ?? "",
        };
      }),
    );
  };
  return (
    <section className={formStyles.section}>
      <h2 className={formStyles.sectionHeading}>Grade-wise student count</h2>
      {isGradeListLoading ? (
        <Loader />
      ) : gradeOptions.length > 0 ? (
        <>
          <Autocomplete
            multiple
            disableCloseOnSelect
            className={styles.picker}
            options={gradeOptions}
            value={selectedOptions}
            filterSelectedOptions
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, selected) =>
              idsMatch(option.value, selected.value)
            }
            onChange={handleSelectionChange}
            noOptionsText={
              selectedOptions.length > 0
                ? "All grades have been added"
                : "No grades available"
            }
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add grades"
                placeholder="Search and select grades"
                variant="outlined"
                fullWidth
              />
            )}
          />
          {selectedOptions.length > 0 ? (
            <div className={styles.gradeChipPanel}>
              <div className={styles.gradeChipGrid}>
                {selectedOptions.map((option) => {
                  const { rowErrors, rowTouched } = getRowMeta(option.value);
                  const hasCountError = showError(
                    rowTouched?.studentCount,
                    rowErrors?.studentCount,
                  );
                  return (
                    <div
                      key={option.value}
                      className={`${styles.gradeChip} ${
                        hasCountError ? styles.gradeChipError : ""
                      } ${
                        option.studentCount !== "" && option.studentCount != null
                          ? styles.gradeChipFilled
                          : ""
                      }`}
                    >
                      <span className={styles.gradeChipLabel}>{option.label}</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={styles.gradeChipInput}
                        value={option.studentCount ?? ""}
                        placeholder="0"
                        aria-label={`Student count for ${option.label}`}
                        onChange={(event) => {
                          const index = findGradeRowIndex(
                            values.gradewiseStudentCount,
                            option.value,
                          );
                          if (index === -1) return;
                          setFieldValue(
                            `gradewiseStudentCount.${index}.studentCount`,
                            sanitizeGradeCountInput(event.target.value),
                            true,
                          );
                        }}
                        onBlur={() => {
                          const index = findGradeRowIndex(
                            values.gradewiseStudentCount,
                            option.value,
                          );
                          if (index === -1) return;
                          handleBlur({
                            target: {
                              name: `gradewiseStudentCount.${index}.studentCount`,
                            },
                          });
                        }}
                      />
                      <button
                        type="button"
                        className={styles.gradeChipRemove}
                        onClick={() =>
                          updateGradeRows(
                            values.gradewiseStudentCount.filter(
                              (row) => !idsMatch(row.gradeId, option.value),
                            ),
                          )
                        }
                        aria-label={`Remove ${option.label}`}
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <p className={styles.gradeEmptyHint}>No grades available.</p>
      )}
      {isErrorOnGradeList && !errors.gradewiseStudentCount ? (
        <ErrorMessage>
          {errorMessageOnFetchGradeList?.message || "Failed to load grades"}
        </ErrorMessage>
      ) : null}
    </section>
  );
};

export default GradeWiseFields;
